import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/auth';
import { useProfile } from '@/contexts/profile';
import { usePreload } from '@/contexts/PreloadContext';

export const useRoutePreloader = () => {
  const { user, session } = useAuth();
  const { activeProfile } = useProfile();
  const { startPreloading, cancelPreloading, preloadDashboardData } = usePreload();
  const hasTriggeredPreload = useRef(false);
  const hasTriggeredDataPreload = useRef(false);

  useEffect(() => {
    // Reset preload trigger when user logs out
    if (!user || !session) {
      hasTriggeredPreload.current = false;
      hasTriggeredDataPreload.current = false;
      cancelPreloading();
      return;
    }

    // Start preloading after successful login (only once per session)
    if (user && session && !hasTriggeredPreload.current) {
      hasTriggeredPreload.current = true;
      
      // Start preloading routes immediately after authentication
      startPreloading();
    }
  }, [user, session, startPreloading, cancelPreloading]);

  // Preload dashboard data when profile is available
  useEffect(() => {
    if (user && session && activeProfile && !hasTriggeredDataPreload.current) {
      hasTriggeredDataPreload.current = true;
      
      // Use requestIdleCallback for non-blocking preload
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          preloadDashboardData(activeProfile.id);
        }, { timeout: 2000 });
      } else {
        // Fallback with small delay
        setTimeout(() => {
          preloadDashboardData(activeProfile.id);
        }, 100);
      }
    }
  }, [user, session, activeProfile, preloadDashboardData]);

  return {
    // Can expose additional functionality if needed
  };
};